import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { feedbackApi, feedbackTypeApi, reportApi } from '../../api/feedback';

// 异步thunk actions
export const fetchFeedbackList = createAsyncThunk(
  'feedback/fetchFeedbackList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await feedbackApi.getFeedbackList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取反馈列表失败');
    }
  }
);

export const fetchFeedbackDetail = createAsyncThunk(
  'feedback/fetchFeedbackDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await feedbackApi.getFeedbackDetail(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取反馈详情失败');
    }
  }
);

export const processFeedback = createAsyncThunk(
  'feedback/processFeedback',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await feedbackApi.processFeedback(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '处理反馈失败');
    }
  }
);

export const fetchFeedbackTypeList = createAsyncThunk(
  'feedback/fetchFeedbackTypeList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await feedbackTypeApi.getFeedbackTypeList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取反馈类型列表失败');
    }
  }
);

export const createFeedbackType = createAsyncThunk(
  'feedback/createFeedbackType',
  async (data, { rejectWithValue }) => {
    try {
      const response = await feedbackTypeApi.createFeedbackType(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '创建反馈类型失败');
    }
  }
);

export const updateFeedbackType = createAsyncThunk(
  'feedback/updateFeedbackType',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await feedbackTypeApi.updateFeedbackType(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '更新反馈类型失败');
    }
  }
);

export const deleteFeedbackType = createAsyncThunk(
  'feedback/deleteFeedbackType',
  async (id, { rejectWithValue }) => {
    try {
      await feedbackTypeApi.deleteFeedbackType(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || '删除反馈类型失败');
    }
  }
);

export const fetchReportList = createAsyncThunk(
  'feedback/fetchReportList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await reportApi.getReportList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取举报列表失败');
    }
  }
);

export const fetchReportDetail = createAsyncThunk(
  'feedback/fetchReportDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await reportApi.getReportDetail(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取举报详情失败');
    }
  }
);

export const auditReport = createAsyncThunk(
  'feedback/auditReport',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await reportApi.auditReport(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '审核举报失败');
    }
  }
);

export const processReport = createAsyncThunk(
  'feedback/processReport',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await reportApi.processReport(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '处理举报失败');
    }
  }
);

export const fetchFeedbackStats = createAsyncThunk(
  'feedback/fetchFeedbackStats',
  async (params, { rejectWithValue }) => {
    try {
      const response = await feedbackApi.getFeedbackStats(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取反馈统计失败');
    }
  }
);

export const fetchReportStats = createAsyncThunk(
  'feedback/fetchReportStats',
  async (params, { rejectWithValue }) => {
    try {
      const response = await reportApi.getReportStats(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取举报统计失败');
    }
  }
);

// 初始状态
const initialState = {
  // 反馈列表
  feedbackList: [],
  feedbackPagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  feedbackLoading: false,
  feedbackError: null,

  // 反馈详情
  feedbackDetail: null,
  detailLoading: false,
  detailError: null,

  // 反馈类型
  feedbackTypes: [],
  typeLoading: false,
  typeError: null,

  // 举报列表
  reportList: [],
  reportPagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  reportLoading: false,
  reportError: null,

  // 举报详情
  reportDetail: null,
  reportDetailLoading: false,
  reportDetailError: null,

  // 统计数据
  feedbackStats: null,
  reportStats: null,
  statsLoading: false,
  statsError: null,

  // UI状态
  modals: {
    feedbackDetail: false,
    feedbackProcess: false,
    typeForm: false,
    reportDetail: false,
    reportAudit: false,
    reportProcess: false,
  },
};

// 创建slice
const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    // 重置错误状态
    clearError: (state) => {
      state.feedbackError = null;
      state.detailError = null;
      state.typeError = null;
      state.reportError = null;
      state.reportDetailError = null;
      state.statsError = null;
    },

    // 设置分页
    setFeedbackPagination: (state, action) => {
      state.feedbackPagination = { ...state.feedbackPagination, ...action.payload };
    },

    setReportPagination: (state, action) => {
      state.reportPagination = { ...state.reportPagination, ...action.payload };
    },

    // 设置模态框状态
    setModalVisible: (state, action) => {
      const { modal, visible } = action.payload;
      if (state.modals.hasOwnProperty(modal)) {
        state.modals[modal] = visible;
      }
    },

    // 重置状态
    resetFeedbackState: (state) => {
      state.feedbackList = [];
      state.feedbackDetail = null;
      state.feedbackPagination = initialState.feedbackPagination;
    },

    resetReportState: (state) => {
      state.reportList = [];
      state.reportDetail = null;
      state.reportPagination = initialState.reportPagination;
    },
  },
  extraReducers: (builder) => {
    builder
      // 反馈列表
      .addCase(fetchFeedbackList.pending, (state) => {
        state.feedbackLoading = true;
        state.feedbackError = null;
      })
      .addCase(fetchFeedbackList.fulfilled, (state, action) => {
        state.feedbackLoading = false;
        state.feedbackList = action.payload.data || [];
        state.feedbackPagination.total = action.payload.total || 0;
      })
      .addCase(fetchFeedbackList.rejected, (state, action) => {
        state.feedbackLoading = false;
        state.feedbackError = action.payload;
      })

      // 反馈详情
      .addCase(fetchFeedbackDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchFeedbackDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.feedbackDetail = action.payload;
      })
      .addCase(fetchFeedbackDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
      })

      // 处理反馈
      .addCase(processFeedback.fulfilled, (state, action) => {
        // 更新列表中的反馈状态
        const index = state.feedbackList.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.feedbackList[index] = { ...state.feedbackList[index], ...action.payload };
        }
        // 更新详情
        if (state.feedbackDetail && state.feedbackDetail.id === action.payload.id) {
          state.feedbackDetail = { ...state.feedbackDetail, ...action.payload };
        }
      })

      // 反馈类型列表
      .addCase(fetchFeedbackTypeList.pending, (state) => {
        state.typeLoading = true;
        state.typeError = null;
      })
      .addCase(fetchFeedbackTypeList.fulfilled, (state, action) => {
        state.typeLoading = false;
        state.feedbackTypes = action.payload.data || [];
      })
      .addCase(fetchFeedbackTypeList.rejected, (state, action) => {
        state.typeLoading = false;
        state.typeError = action.payload;
      })

      // 创建反馈类型
      .addCase(createFeedbackType.fulfilled, (state, action) => {
        state.feedbackTypes.push(action.payload);
      })

      // 更新反馈类型
      .addCase(updateFeedbackType.fulfilled, (state, action) => {
        const index = state.feedbackTypes.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.feedbackTypes[index] = action.payload;
        }
      })

      // 删除反馈类型
      .addCase(deleteFeedbackType.fulfilled, (state, action) => {
        state.feedbackTypes = state.feedbackTypes.filter(item => item.id !== action.payload);
      })

      // 举报列表
      .addCase(fetchReportList.pending, (state) => {
        state.reportLoading = true;
        state.reportError = null;
      })
      .addCase(fetchReportList.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.reportList = action.payload.data || [];
        state.reportPagination.total = action.payload.total || 0;
      })
      .addCase(fetchReportList.rejected, (state, action) => {
        state.reportLoading = false;
        state.reportError = action.payload;
      })

      // 举报详情
      .addCase(fetchReportDetail.pending, (state) => {
        state.reportDetailLoading = true;
        state.reportDetailError = null;
      })
      .addCase(fetchReportDetail.fulfilled, (state, action) => {
        state.reportDetailLoading = false;
        state.reportDetail = action.payload;
      })
      .addCase(fetchReportDetail.rejected, (state, action) => {
        state.reportDetailLoading = false;
        state.reportDetailError = action.payload;
      })

      // 审核举报
      .addCase(auditReport.fulfilled, (state, action) => {
        // 更新列表中的举报状态
        const index = state.reportList.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.reportList[index] = { ...state.reportList[index], ...action.payload };
        }
        // 更新详情
        if (state.reportDetail && state.reportDetail.id === action.payload.id) {
          state.reportDetail = { ...state.reportDetail, ...action.payload };
        }
      })

      // 处理举报
      .addCase(processReport.fulfilled, (state, action) => {
        // 更新列表中的举报状态
        const index = state.reportList.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.reportList[index] = { ...state.reportList[index], ...action.payload };
        }
        // 更新详情
        if (state.reportDetail && state.reportDetail.id === action.payload.id) {
          state.reportDetail = { ...state.reportDetail, ...action.payload };
        }
      })

      // 统计数据
      .addCase(fetchFeedbackStats.fulfilled, (state, action) => {
        state.feedbackStats = action.payload;
      })
      .addCase(fetchReportStats.fulfilled, (state, action) => {
        state.reportStats = action.payload;
      });
  },
});

// 导出actions
export const {
  clearError,
  setFeedbackPagination,
  setReportPagination,
  setModalVisible,
  resetFeedbackState,
  resetReportState,
} = feedbackSlice.actions;

// 导出reducer
export default feedbackSlice.reducer; 